import { useEffect, useRef, useState, useCallback } from "react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  UploadSimple, FilePdf, FileText, Trash, PaperPlaneRight, Sparkle, Books, User, Info, Clock,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function bytesToSize(bytes) {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB"];
  const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
}

export default function KnowledgeBase() {
  const { currentOrg } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [messages, setMessages] = useState([]); // { role: 'user'|'assistant', text, sources? }
  const [question, setQuestion] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [detailDoc, setDetailDoc] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);

  const openDetail = async (d) => {
    setDetailDoc({ doc_id: d.doc_id, filename: d.filename, loading: true });
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/knowledge-base/documents/${d.doc_id}`);
      setDetailDoc(data);
    } catch {
      toast.error("Could not load document detail");
      setDetailDoc(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const loadDocs = useCallback(async () => {
    setLoadingDocs(true);
    try {
      const { data } = await api.get("/knowledge-base/documents");
      setDocs(data);
    } catch (e) {
      toast.error("Could not load documents");
    } finally {
      setLoadingDocs(false);
    }
  }, []);

  useEffect(() => { loadDocs(); }, [loadDocs, currentOrg?.org_id]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["pdf", "txt"].includes(ext)) {
      toast.error("Only PDF and TXT files are supported");
      e.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File exceeds 10MB limit");
      e.target.value = "";
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post("/knowledge-base/documents", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${file.name} uploaded`);
      await loadDocs();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Upload failed";
      toast.error(detail);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.filename}"? This removes it from the knowledge base.`)) return;
    try {
      await api.delete(`/knowledge-base/documents/${doc.doc_id}`);
      setDocs((d) => d.filter((x) => x.doc_id !== doc.doc_id));
      toast.success("Document removed");
    } catch {
      toast.error("Could not delete document");
    }
  };

  const handleAsk = async (e) => {
    e.preventDefault();
    const q = question.trim();
    if (!q || streaming) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setQuestion("");
    setStreaming(true);
    // Append empty assistant placeholder
    setMessages((m) => [...m, { role: "assistant", text: "", sources: [] }]);

    try {
      const res = await fetch(`${API_BASE}/knowledge-base/ask`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: q }),
      });
      if (!res.ok || !res.body) {
        const t = await res.text().catch(() => "");
        throw new Error(t || `Request failed (${res.status})`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let done = false;
      while (!done) {
        const chunk = await reader.read();
        done = chunk.done;
        if (done) break;
        buffer += decoder.decode(chunk.value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const evt = JSON.parse(payload);
            if (evt.type === "text") {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last && last.role === "assistant") {
                  copy[copy.length - 1] = { ...last, text: (last.text || "") + evt.content };
                }
                return copy;
              });
            } else if (evt.type === "sources") {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last && last.role === "assistant") {
                  copy[copy.length - 1] = { ...last, sources: evt.sources || [] };
                }
                return copy;
              });
            } else if (evt.type === "error") {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last && last.role === "assistant") {
                  copy[copy.length - 1] = { ...last, text: "The assistant is temporarily unavailable." };
                }
                return copy;
              });
            }
          } catch {
            /* ignore malformed frame */
          }
        }
      }
    } catch (err) {
      toast.error("Could not get an answer");
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant" && !last.text) {
          copy[copy.length - 1] = { ...last, text: "Something went wrong. Please try again." };
        }
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="p-8 md:p-10 max-w-[1400px]" data-testid="knowledge-base-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary">
            {currentOrg?.name || "Workspace"}
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">Knowledge Base</h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-lg">
            Upload PDF or text files. Ask questions and get answers grounded in your workspace&rsquo;s documents.
          </p>
        </div>
        <div className="hidden md:flex items-center gap-2 text-[11px] uppercase tracking-widest text-ink-secondary border border-white/10 rounded-full px-3 py-1.5">
          <Books weight="duotone" className="h-3.5 w-3.5" />
          {docs.length} {docs.length === 1 ? "document" : "documents"}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: documents */}
        <section className="lg:col-span-5 rounded-md border border-white/10 bg-graphite-900 flex flex-col min-h-[560px]" data-testid="kb-documents-panel">
          <div className="p-5 border-b border-white/10 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary">Documents</div>
              <div className="mt-1 text-sm text-ink-primary">Upload PDF or TXT · up to 10&nbsp;MB</div>
            </div>
            <button
              data-testid="kb-upload-button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-200 disabled:opacity-60 transition-colors"
            >
              <UploadSimple weight="bold" className="h-4 w-4" />
              {uploading ? "Uploading…" : "Upload"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.txt,application/pdf,text/plain"
              onChange={handleFile}
              className="hidden"
              data-testid="kb-file-input"
            />
          </div>

          <div className="flex-1 overflow-y-auto" data-testid="kb-documents-list">
            {loadingDocs ? (
              <div className="p-8 text-center text-ink-secondary text-sm">Loading documents…</div>
            ) : docs.length === 0 ? (
              <div className="p-10 text-center">
                <div className="mx-auto h-12 w-12 rounded-md border border-dashed border-white/15 grid place-items-center">
                  <Books weight="duotone" className="h-5 w-5 text-ink-secondary" />
                </div>
                <div className="mt-4 font-display text-xl tracking-tight">No documents yet</div>
                <p className="mt-2 text-sm text-ink-secondary max-w-xs mx-auto">
                  Upload a PDF or plain text file to start building your workspace&rsquo;s knowledge base.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-white/5">
                {docs.map((d) => (
                  <li key={d.doc_id} className="p-4 flex items-center gap-3 group" data-testid={`kb-doc-row-${d.doc_id}`}>
                    <div className="h-10 w-10 shrink-0 rounded-sm border border-white/10 bg-graphite-800 grid place-items-center">
                      {d.file_type === "pdf" ? (
                        <FilePdf weight="duotone" className="h-5 w-5 text-ink-primary" />
                      ) : (
                        <FileText weight="duotone" className="h-5 w-5 text-ink-primary" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm text-ink-primary flex items-center gap-2" title={d.filename} data-testid={`kb-doc-filename-${d.doc_id}`}>
                        <span className="truncate">{d.filename}</span>
                        {(d.version || 1) > 1 && (
                          <span className="text-[9px] uppercase tracking-widest border border-white/10 px-1.5 py-0.5 rounded-sm text-ink-secondary">v{d.version}</span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-[11px] text-ink-secondary">
                        <span className="uppercase tracking-widest">{d.file_type}</span>
                        <span className="opacity-40">·</span>
                        <span>{bytesToSize(d.size)}</span>
                        <span className="opacity-40">·</span>
                        <span className="inline-flex items-center gap-1">
                          <User weight="regular" className="h-3 w-3" />
                          {d.uploaded_by_name || "—"}
                        </span>
                        <span className="opacity-40">·</span>
                        <span>{formatDate(d.created_at)}</span>
                        {d.embedding_status && d.embedding_status !== "ready" && (
                          <>
                            <span className="opacity-40">·</span>
                            <span className="text-amber-300/80 uppercase tracking-widest">{d.embedding_status}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <button
                      data-testid={`kb-doc-view-${d.doc_id}`}
                      onClick={() => openDetail(d)}
                      className="opacity-60 hover:opacity-100 p-2 rounded-md hover:bg-white/5 transition-colors"
                      title="View summary and versions"
                    >
                      <Info weight="bold" className="h-4 w-4" />
                    </button>
                    <button
                      data-testid={`kb-doc-delete-${d.doc_id}`}
                      onClick={() => handleDelete(d)}
                      className="opacity-60 hover:opacity-100 hover:text-accent-danger p-2 rounded-md hover:bg-white/5 transition-colors"
                      title="Delete document"
                    >
                      <Trash weight="bold" className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Right: ask */}
        <section className="lg:col-span-7 rounded-md border border-white/10 bg-graphite-900 flex flex-col min-h-[560px]" data-testid="kb-ask-panel">
          <div className="p-5 border-b border-white/10 flex items-center gap-3">
            <div className="h-8 w-8 rounded-sm bg-graphite-800 border border-white/10 grid place-items-center">
              <Sparkle weight="duotone" className="h-4 w-4 text-ink-primary" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary">Ask</div>
              <div className="mt-0.5 text-sm text-ink-primary">Answers grounded in this workspace only</div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="kb-messages">
            {messages.length === 0 ? (
              <div className="h-full min-h-[300px] grid place-items-center text-center">
                <div className="max-w-sm">
                  <div className="mx-auto h-12 w-12 rounded-md border border-dashed border-white/15 grid place-items-center">
                    <Sparkle weight="duotone" className="h-5 w-5 text-ink-secondary" />
                  </div>
                  <div className="mt-4 font-display text-xl tracking-tight">Ask your knowledge base</div>
                  <p className="mt-2 text-sm text-ink-secondary">
                    Try: <span className="text-ink-primary/80">&ldquo;Summarize the pricing document&rdquo;</span> or <span className="text-ink-primary/80">&ldquo;What is our refund policy?&rdquo;</span>
                  </p>
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`kb-message-${m.role}-${i}`}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-md px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${
                      m.role === "user"
                        ? "bg-white text-graphite-950"
                        : "bg-graphite-800 border border-white/10 text-ink-primary"
                    }`}
                  >
                    {m.text || (streaming && i === messages.length - 1 ? (
                      <span className="inline-flex gap-1 items-center text-ink-secondary">
                        <span className="h-1.5 w-1.5 rounded-full bg-ink-secondary animate-pulse" />
                        <span className="h-1.5 w-1.5 rounded-full bg-ink-secondary animate-pulse [animation-delay:120ms]" />
                        <span className="h-1.5 w-1.5 rounded-full bg-ink-secondary animate-pulse [animation-delay:240ms]" />
                      </span>
                    ) : null)}
                    {m.role === "assistant" && m.sources && m.sources.length > 0 && m.text && (
                      <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap gap-1.5">
                        <span className="text-[10px] uppercase tracking-widest text-ink-secondary">Sources</span>
                        {m.sources.map((s) => (
                          <span
                            key={s.doc_id}
                            className="text-[11px] px-2 py-0.5 rounded-full bg-graphite-950 border border-white/10 text-ink-secondary"
                          >
                            {s.filename}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
            <div ref={chatEndRef} />
          </div>

          <form onSubmit={handleAsk} className="border-t border-white/10 p-4 flex items-center gap-3">
            <input
              data-testid="kb-question-input"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={docs.length === 0 ? "Upload a document first…" : "Ask a question about your documents…"}
              className="flex-1 bg-graphite-800 border border-white/10 rounded-md px-4 py-2.5 text-sm text-ink-primary placeholder:text-ink-secondary/60 focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/30 transition-colors"
              disabled={streaming}
            />
            <button
              data-testid="kb-ask-submit"
              type="submit"
              disabled={streaming || !question.trim()}
              className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2.5 text-sm font-medium hover:bg-neutral-200 disabled:opacity-50 transition-colors"
            >
              <PaperPlaneRight weight="bold" className="h-4 w-4" />
              {streaming ? "Thinking…" : "Ask"}
            </button>
          </form>
        </section>
      </div>

      <Dialog open={!!detailDoc} onOpenChange={(v) => !v && setDetailDoc(null)}>
        <DialogContent className="border-white/10 bg-graphite-800 text-ink-primary max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="kb-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-light tracking-tight text-2xl flex items-center gap-2 flex-wrap">
              <span>{detailDoc?.filename}</span>
              {(detailDoc?.version || 1) > 1 && (
                <span className="text-[10px] uppercase tracking-widest border border-white/10 px-1.5 py-0.5 rounded-sm text-ink-secondary">v{detailDoc?.version}</span>
              )}
            </DialogTitle>
            <DialogDescription className="text-ink-secondary text-sm">
              Structured summary and version history for this document.
            </DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="text-center text-sm text-ink-secondary py-8">Loading…</div>
          ) : detailDoc ? (
            <div className="space-y-6">
              <section data-testid="kb-detail-summary">
                <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary mb-2">Summary</div>
                {detailDoc.summary ? (
                  <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-ink-primary/90 bg-graphite-900 border border-white/10 rounded-md p-4">{detailDoc.summary}</pre>
                ) : (
                  <div className="text-sm text-ink-secondary italic">Summary not yet generated (embedding status: {detailDoc.embedding_status || "pending"}).</div>
                )}
              </section>
              <section data-testid="kb-detail-versions">
                <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary mb-2">Version history</div>
                <ul className="space-y-2">
                  {(detailDoc.versions || []).map((v) => (
                    <li key={v.doc_id} data-testid={`kb-detail-version-row-${v.version}`} className={`rounded-md border p-3 flex items-center gap-3 ${v.is_current ? "border-white/20 bg-graphite-900" : "border-white/5 bg-graphite-900/50"}`}>
                      <div className="h-8 w-8 rounded-sm border border-white/10 bg-graphite-800 grid place-items-center">
                        <Clock weight="duotone" className="h-4 w-4 text-ink-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-ink-primary flex items-center gap-2">
                          <span>Version {v.version}</span>
                          {v.is_current && <span className="text-[9px] uppercase tracking-widest border border-emerald-400/30 bg-emerald-400/10 text-emerald-300 px-1.5 py-0.5 rounded-sm">Current</span>}
                        </div>
                        <div className="text-[11px] text-ink-secondary">
                          {formatDate(v.created_at)} · {v.uploaded_by_name || "—"} · {v.chunk_count} chunks
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
